import { ExtractionResult, FieldConfidence, PCCase } from '../types';

const API_BASE = import.meta.env.VITE_API_URL || '/api';

/**
 * Extract case data from page content using AI
 */
export async function extractCaseData(
  content: string
): Promise<ExtractionResult> {
  try {
    const response = await fetch(`${API_BASE}/extract`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ content }),
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      return {
        success: false,
        data: {},
        confidenceScores: [],
        error: error.error || `Server error: ${response.status}`,
      };
    }

    const { rawResponse } = await response.json();
    return parseExtractionResponse(rawResponse);
  } catch (error) {
    return {
      success: false,
      data: {},
      confidenceScores: [],
      error: error instanceof Error ? error.message : 'Failed to extract data',
    };
  }
}

/**
 * Parse the AI response into structured data
 */
function parseExtractionResponse(rawResponse: string): ExtractionResult {
  try {
    // Try to extract JSON from the response
    let jsonStr = rawResponse;

    // Handle markdown code blocks
    const jsonMatch = rawResponse.match(/```(?:json)?\s*([\s\S]*?)```/);
    if (jsonMatch) {
      jsonStr = jsonMatch[1];
    }

    const parsed = JSON.parse(jsonStr);

    const data = parsed.data || parsed;
    const confidenceScores: FieldConfidence[] = parsed.confidenceScores || [];

    // Validate and clean the extracted data
    const cleanedData = cleanExtractedData(data);

    return {
      success: true,
      data: cleanedData,
      confidenceScores,
      rawResponse,
    };
  } catch (error) {
    return {
      success: false,
      data: {},
      confidenceScores: [],
      rawResponse,
      error: 'Failed to parse AI response as JSON',
    };
  }
}

/**
 * Clean and validate extracted data
 */
function cleanExtractedData(data: Record<string, unknown>): Partial<PCCase> {
  const cleaned: Partial<PCCase> = {};

  // String fields
  if (typeof data.name === 'string' && data.name.trim()) {
    cleaned.name = data.name.trim();
  }
  if (typeof data.manufacturer === 'string' && data.manufacturer.trim()) {
    cleaned.manufacturer = data.manufacturer.trim();
  }
  if (typeof data.description === 'string' && data.description.trim()) {
    cleaned.description = data.description.trim();
  }
  if (typeof data.imageUrl === 'string' && data.imageUrl.startsWith('http')) {
    cleaned.imageUrl = data.imageUrl.trim();
  }

  // Size category
  const validSizes = ['Full-Tower', 'Mid-Tower', 'Mini-Tower', 'SFF', 'HTPC'];
  if (validSizes.includes(data.sizeCategory as string)) {
    cleaned.sizeCategory = data.sizeCategory as PCCase['sizeCategory'];
  }

  // Side panel
  const validPanels = ['Tempered Glass', 'Acrylic', 'Steel', 'Mesh'];
  if (Array.isArray(data.sidePanel)) {
    cleaned.sidePanel = data.sidePanel.filter(
      (p) => validPanels.includes(p as string)
    ) as string[];
  } else if (validPanels.includes(data.sidePanel as string)) {
    cleaned.sidePanel = [data.sidePanel as string];
  }

  // Dimensions
  if (data.dimensions && typeof data.dimensions === 'object') {
    const dims = data.dimensions as Record<string, unknown>;
    cleaned.dimensions = {
      height: parseNumber(dims.height) || 0,
      width: parseNumber(dims.width) || 0,
      depth: parseNumber(dims.depth) || 0,
    };
  }

  // Numeric fields
  const numericFields = [
    'weight',
    'maxGpuLength',
    'maxCpuCoolerHeight',
    'maxPsuLength',
    'releaseYear',
  ] as const;

  for (const field of numericFields) {
    const value = parseNumber(data[field]);
    if (value !== null && value > 0) {
      (cleaned as Record<string, unknown>)[field] = value;
    }
  }

  // Form factor support
  if (Array.isArray(data.formFactorSupport)) {
    const validFormFactors = ['E-ATX', 'ATX', 'Micro-ATX', 'Mini-ITX', 'Mini-DTX'];
    cleaned.formFactorSupport = data.formFactorSupport.filter(
      (ff) => validFormFactors.includes(ff as string)
    ) as PCCase['formFactorSupport'];
  }

  // Colors
  if (Array.isArray(data.colors)) {
    cleaned.colors = data.colors.filter(
      (c) => typeof c === 'string' && c.trim()
    ) as string[];
  }

  // Features
  if (Array.isArray(data.features)) {
    cleaned.features = data.features.filter(
      (f) => typeof f === 'string' && f.trim()
    ) as string[];
  }

  // Fan mounts
  if (Array.isArray(data.fanMounts)) {
    cleaned.fanMounts = data.fanMounts
      .filter((fm) => fm && typeof fm === 'object')
      .map((fm) => {
        const mount = fm as Record<string, unknown>;
        return {
          location: mount.location as PCCase['fanMounts'][0]['location'],
          sizes: Array.isArray(mount.sizes) ? mount.sizes.map(Number).filter(Boolean) : [],
          maxFans: parseNumber(mount.maxFans) || 0,
          radiatorSupport: Array.isArray(mount.radiatorSupport)
            ? mount.radiatorSupport.map(Number).filter(Boolean)
            : undefined,
        };
      })
      .filter((fm) => fm.location && fm.sizes.length > 0);
  }

  // Drive bays
  if (data.driveBays && typeof data.driveBays === 'object') {
    const bays = data.driveBays as Record<string, unknown>;
    cleaned.driveBays = {
      internal35: parseNumber(bays.internal35) || 0,
      internal25: parseNumber(bays.internal25) || 0,
      external525: parseNumber(bays.external525) || undefined,
    };
  }

  // I/O Panel
  if (data.ioPanel && typeof data.ioPanel === 'object') {
    const io = data.ioPanel as Record<string, unknown>;
    cleaned.ioPanel = {
      usb3: parseNumber(io.usb3) || undefined,
      usb2: parseNumber(io.usb2) || undefined,
      usbC: parseNumber(io.usbC) || undefined,
      audio: typeof io.audio === 'boolean' ? io.audio : undefined,
      micIn: typeof io.micIn === 'boolean' ? io.micIn : undefined,
    };
  }

  return cleaned;
}

/**
 * Parse a value to a number
 */
function parseNumber(value: unknown): number | null {
  if (typeof value === 'number' && !isNaN(value)) {
    return value;
  }
  if (typeof value === 'string') {
    const num = parseFloat(value.replace(/[^\d.-]/g, ''));
    return isNaN(num) ? null : num;
  }
  return null;
}
